/**
 * NOTE!!!!!!!!!!!!!
 *
 * This file is for sharing types across aha-app <-> whiteboards.
 * It CANNOT import ANY types from a package that is not shared in both package.json files.
 * It CANNOT import ANY application code from either aha-app or whiteboards.
 *
 * This is simply to define shared interfaces for the boundary between aha-app and whiteboards.
 */

import { type useCallback, type useRef } from 'react';

// Generic construtor used by mixins.
export type Constructor<T = {}> = new (...args: any[]) => T;

export type RecordId = `${number}`;
export type RecordReferenceNum = string;
export type RecordRef = RecordId | RecordReferenceNum; // Generic string for now to represent a ReferenceNum
export type ModelName = Aha.ModelUnion['typename'];
export type RecordIdentifier = `${ModelName}-${RecordId}`;

export interface WorkflowKind {
  readonly id: RecordId;
  readonly name: string;
  readonly featureTemplate: WorkflowKindTemplate | null;
}

export interface WorkflowKindTemplate {
  readonly id: RecordId;
  readonly body: string | null;
}

// Data structure to reference any record
export interface RecordReferenceStruct {
  id: RecordId | RecordReferenceNum;
  typename: ModelName;
}

interface UndefinedWhiteboardRecordInfo {
  typename: 'Undefined';
  id?: RecordId;
  name?: string;
  referenceNum?: string;
}

interface NotFoundWhiteboardRecordInfo {
  typename: 'NotFound';
  id?: RecordId;
  name?: string;
  referenceNum?: string;
}

export const WHITEBOARD_RECORD_TYPES = [
  'Epic',
  'Feature',
  'Goal',
  'Idea',
  'IdeaTheme',
  'Initiative',
  'Release',
  'ReleasePhase',
  'Requirement',
  'KeyResult',
  'Task',
  'Page',
  'PdlcStep',
  'Project',
  'Annotation',
] as const;
export type WhiteboardRecordTypes = (typeof WHITEBOARD_RECORD_TYPES)[number];

export type WhiteboardRecordModels = Extract<
  Aha.ModelUnion,
  { typename: WhiteboardRecordTypes }
> & { referenceNum?: string };

export type WhiteboardRecordWithLinks = Extract<
  WhiteboardRecordInfo,
  { parentRecordLinks: any }
>;

export type WhiteboardRecordTypeNames = `${Uncapitalize<
  WhiteboardRecordModels['typename']
>}s`;

export type WhiteboardRecordInfo =
  | WhiteboardRecordModels
  | UndefinedWhiteboardRecordInfo
  | NotFoundWhiteboardRecordInfo;

export interface RecordLookupOptions {
  fetchRecordLinks?: boolean;
  cardView?: Aha.CardView;
}

export type RecordLookupType =
  | WhiteboardRecordTypeNames
  | WhiteboardRecordModels['typename'];

export type ResolveRecordInfo = (
  recordType: RecordLookupType,
  recordId: RecordId,
  options?: RecordLookupOptions
) => Promise<WhiteboardRecordInfo>;

export type SmartZoneAttributes = Record<string, unknown>;

export interface RecordUpdate {
  recordType: RecordLookupType;
  recordId: string;
  attributes: SmartZoneAttributes;
}

export interface RecordUpdateResponse {
  success: boolean;
  warnings?: string[];
  results: Array<{
    recordType: string;
    recordId: string;
    success: boolean;
    errors?: Error[];
    warnings?: string[];
  }>;
}

interface Opt {
  value: string;
  text: string;
}

export type ResolveOptions = (value: string[]) => Promise<Opt[] | undefined>;

export type SmartZoneField = {
  name: string;
  description: string;
} & (
  | {
      fieldType: 'text';
      fetchUrl?: never;
    }
  | ({
      fieldType: 'select' | 'multi-select';
      branchesSelectable?: boolean;
    } & (
      | {
          fetchUrl: string;
          resolveOptions: ResolveOptions;
        }
      | {
          // if fetch returns different options based on the form state, getKey must
          // return a unique key for each distinct set of options, to ensure the field resets
          fetch: (formState: SmartZoneAttributes | undefined) => Promise<Opt[]>;
          getKey?: (formState: SmartZoneAttributes | undefined) => string;
        }
    ))
);

export interface RecordTypeData {
  recordType: string;
  name: string;
  fields: SmartZoneField[];
}

export type SmartZoneFormData = RecordTypeData[];

export type UpdateRecords = (
  records: RecordUpdate[]
) => Promise<RecordUpdateResponse>;

export interface RecordInterface {
  resolveRecordInfo?: ResolveRecordInfo;
  updateRecords?: UpdateRecords;
  openAhaDrawer?: ({
    url,
    useBottomDrawer,
  }: {
    url: string;
    useBottomDrawer?: boolean;
  }) => void;
  openRecordInDrawer?: ({
    recordRef,
    useBottomDrawer,
  }: {
    recordRef: RecordReferenceStruct;
    useBottomDrawer?: boolean;
  }) => void;
}

export type CardLayoutRecordTypeName = Aha.CardLayoutRecordTypesEnum['values'];

export type CardViewType = 'expanded' | 'collapsed' | 'whiteboard';

export type RecordLayoutKey =
  `${Uncapitalize<CardLayoutRecordTypeName>}RecordLayout`;

export interface WhiteboardCardLayoutEditorOptions {
  callback: (cardLayout: Aha.CardLayout) => Promise<void>;
  cardLayout: Aha.CardLayout;
  currentRecordType?: CardLayoutRecordTypeName;
  visibleRecordTypes: CardLayoutRecordTypeName[];
}

export type WhiteboardCardLayoutEditorFunction = (
  options: WhiteboardCardLayoutEditorOptions
) => Promise<Aha.CardLayout | undefined | null>;

export type WhiteboardLoadDefaultCardLayoutFunction =
  () => Promise<Aha.CardLayout | null>;

// Side panel types
export interface SidePanelItem {
  id: string;
  name: string;
  icon: React.ReactElement | string;
  alwaysRendered?: boolean;
  // The state is from the controller, but we can't import those types into this shared file
  hidden?: (state: Record<string, any>) => boolean;
  tabContent?: React.ReactNode;
  // Extra content to render next to the title
  titleContent?: React.ReactNode;
  // Extra content to render beneath the title
  headerContent?: React.ReactNode;
  // Extra content to render as a fixed footer
  footerContent?: React.ReactNode;
  // Content to render in the panel
  content?: React.ReactNode;
  // A full panel, rendering title and content. Replaces content and titleContent.
  panel?: React.ReactNode;
  // A link to a promo page if the account doesn't have the required flavor
  promoLink?: PromoLinkType;
  // Callback when the item is opened
  onOpen?: () => void;
  // Callback when the item is closed
  onClose?: () => void;
  contentClassName?: string;
  testId?: string;
}

export type PromoLinkType = string | (() => PromoLinkValue);
export type PromoLinkValue = string | undefined;

export interface SidePanelExternalState {
  visible: boolean;
}

export interface ISidePanelController {
  actionOpenSidePanel: (activeItemId?: string) => void;
  actionCloseSidePanel: () => void;
  actionPushSidePanel: (item: SidePanelItem) => void;
  actionPopSidePanel: () => void;
  actionReplaceSidePanelStack(items: SidePanelItem[]): void;
  actionSetHidden(hidden: boolean): void;
}

type SidePanelStateCallback = (state: SidePanelExternalState) => void;

export interface SidePanelProps {
  sidePanelItems: SidePanelItem[];
  documentType: 'Whiteboard' | 'Note';
  onChange?: SidePanelStateCallback;
  hidden?: boolean;
}

export type SidePanelComponent = React.FC<
  SidePanelProps & {
    controllerRef?: ReturnType<
      | typeof useCallback<(instance: ISidePanelController) => void>
      | typeof useRef<ISidePanelController>
    >;
  }
>;

// Card rendering types
export interface IconStructure {
  name: string;
  color?: string;
  className?: string;
  title?: string;
  backgroundColor?: string;
  rounded?: true;
}

export interface AvatarStructure {
  src: string | null;
  name: string;
  initials: string;
}

export interface TagStructure {
  name: string;
  color: string;
}

export interface PillStructure {
  text: string;
  color?: string;
  textColor?: string;
  icon?: IconStructure;
}

export interface IntegrationContent {
  name: string;
  icon?: string;
  title?: string;
  url: string;
}

export interface FieldContentsData {
  // The icon contents for the field.
  icon?: IconStructure;
  // The avatar contents for the field.
  avatar?: AvatarStructure;
  // An external URL of the field, opens in a new tab. Will be used to wrap individual contents.
  externalUrl?: string;
  // The integrations contents for the field.
  integrations?: IntegrationContent[];
  // The link contents for the field (if multiple links)
  link?: string;
  // The link contents for the field (if multiple links)
  linkIcon?: boolean;
  // The link contents for the field (if multiple links)
  linkIconOnly?: boolean | false;
  // The pill contents for the field.
  pill?: PillStructure;
  // The progress bar percentage for the field.
  progress?: number;
  // The tags contents for the field.
  tags?: TagStructure[];
  // The text contents for the field.
  text?: string | number;
  // The URL of the field. Will be used to wrap individual contents.
  url?: string;
  // The URL of the field. Will be used to wrap individual contents.
  nowrap?: true;
  // Describe if the field is a title.
  titleStyle?: boolean;
  // Reference to a record, used for rendering links (particularly in whiteboards)
  recordRef?: RecordReferenceStruct;
  // The text for a field prefix before the text
  prefix?: string;
}

export interface CardStylingDataStructure {
  // The highlight color to use on the card.
  highlightColor?: string;
  useBackgroundColor?: boolean;
}

// types for external integrations
// TODO these should definitely live in the whiteboard package once that exports types
// keeping here for now so we can reference them from outside the Whiteboard
export interface IntegrationRecordTypeData {
  id: string;
  name: string;
  icon: string;
}

interface IntegrationRecordStatusData {
  id: string;
  name: string;
  category?: Aha.InternalMeaning['values'];
  color?: string;
}

interface IntegrationRecordAssigneeData {
  id: string;
  name: string;
  avatar?: string;
}

interface IntegrationRecordPriorityData {
  id: string;
  name: string;
  icon?: string;
}

export interface IntegrationRecordData {
  id: string;
  // How we identify the record in the external system. This might be distinct
  // from the id and key depending on the remote system. For example, with Jira
  // the keys are unique across projects. In ADO there is no key field and the
  // record is identified by org/project/id
  recordId: string;
  key: string;
  url: string;
  name: string;
  type?: IntegrationRecordTypeData;
  status?: IntegrationRecordStatusData;
  assignee?: IntegrationRecordAssigneeData;
  priority?: IntegrationRecordPriorityData;
}

export interface SuccessResult<R> {
  status: 'success';
  data: R;
}

export interface ErrorResult {
  status: 'error';
  // TODO make this an array for flexibility, so we can combine multiple errors into one
  error: string;
}

export type Result<R> = SuccessResult<R> | ErrorResult;

export interface RequestOptions {
  authenticate?: boolean;
}

type GetRecords = (args: {
  recordUrls: string[];
  options?: RequestOptions;
}) => Promise<Result<IntegrationRecordData[]>>;

export interface WhiteboardIntegration<N = string> {
  service: N;
  title: string;
  getRecordTitle: (plural?: boolean, includeTitle?: boolean) => string;
  getRecords: GetRecords;
  hasAuth: () => boolean;
  urlToRecordId: (url: string) => string | null;
}

interface IntegrationRecordType {
  id: string;
  name: string;
  description: string;
}
export interface IntegrationSite {
  id: string;
  name: string;
}

export interface IntegrationProject {
  id: string;
  key: string;
  name: string;
  recordTypes: IntegrationRecordType[];
}

interface Pages {
  current: number;
  total: number;
}

type GetProjects = (args: {
  siteId: string;
  searchParam?: string;
  page?: number;
  options?: RequestOptions;
}) => Promise<Result<{ projects: IntegrationProject[]; pages?: Pages }>>;

type GetSites = (args: {
  options?: RequestOptions;
}) => Promise<Result<{ sites: IntegrationSite[] }>>;

type IntegrationFieldType =
  | {
      type: 'text';
    }
  | {
      type: 'number';
    }
  | {
      type: 'date';
    }
  | {
      type: 'datetime';
    }
  | {
      type: 'select';
      multiple: boolean;
      options: { value: string; text: string }[] | GetOptions;
    }
  | {
      type: 'tags';
    };

export interface IntegrationRecordField {
  name: string;
  description: string;
  required: boolean;
  type: IntegrationFieldType;
}

export interface CreatedRecordData {
  id: string;
  key: string;
  url: string;
}

type GetRecordFields<K extends Record<string, unknown>> = (args: {
  key: K;
  options?: RequestOptions;
}) => Promise<Result<{ fields: IntegrationRecordField[] }>>;

export type NewRecords = Record<
  string,
  {
    summary: string;
    description?: string;
  }
>;

export type RecordFieldData = Record<string, string | string[]>;

type CreateRecords<K extends Record<string, unknown>> = (args: {
  key: K;
  records: NewRecords; // record details, keyed by whiteboard shape id
  fieldData: RecordFieldData; // corresponds to record field response, values apply to all created records
  options?: RequestOptions;
}) => Promise<Result<{ records: Record<string, CreatedRecordData> }>>;

export interface DropdownOption<T = unknown> {
  [key: string]: unknown;
  value: string;
  text: string;
  item: T;
}

export type GetOptions<T = unknown> = (data: {
  searchParam: string;
  page?: number;
}) => Promise<{
  options: DropdownOption<T>[];
  pages?: {
    current: number;
    total: number;
  };
}>;

export interface WriteCapableWhiteboardIntegration<K extends {}>
  extends WhiteboardIntegration<'jira'> {
  getSites: GetSites;
  getProjects: GetProjects;
  getRecordFields: GetRecordFields<K>;
  createRecords: CreateRecords<K>;
}

export interface JiraRecordTypeKey {
  siteId: string;
  projectId: string;
  issueTypeId: string;
}

export type JiraWhiteboardIntegration =
  WriteCapableWhiteboardIntegration<JiraRecordTypeKey>;

/**
 * For when we create records from objects
 */
export interface CreateRecord {
  id: RecordId;
  text: string;
}

export type CreateableRecords =
  | WhiteboardRecordTypeNames
  | 'integrationRecords';

export interface CreateRecordMeta {
  id: RecordId; // Used to idetnify the shape that the record was created from
}

// Any data for this record. Must conform to expected format in gql requests e.g.,
// epic: { id: '123'}
export interface RecordAttributes {
  [key: string]: unknown;
}

export type CreateRecordParentId =
  | 'projectId'
  | 'releaseId'
  | 'featureId'
  | 'pdlcFrameworkId'; // This is specificed to satisfy pdlcSteps, but it is not used

export type CreateRecordData = {
  meta: CreateRecordMeta;
  attributes?: RecordAttributes;
  name: string;
} & {
  [key in CreateRecordParentId]?: RecordId;
};

export interface IRecordManager {
  canCreateRecordType: (type: CreateableRecords) => boolean;
  createIdeas: (records: CreateRecordData[]) => Promise<any>;
  createFeatures: (records: CreateRecordData[]) => Promise<any>;
  createRequirements: (records: CreateRecordData[]) => Promise<any>;
  createEpics: (records: CreateRecordData[]) => Promise<any>;
  createReleases: (records: CreateRecordData[]) => Promise<any>;
  createGoals: (records: CreateRecordData[]) => Promise<any>;
  createInitiatives: (records: CreateRecordData[]) => Promise<any>;
  createPdlcSteps: (
    records: CreateRecordData[],
    { pdlcFrameworkId }: { pdlcFrameworkId: string }
  ) => Promise<any>;
  getWorkflowKinds(releaseId: RecordId): Promise<WorkflowKind[]>;
}

const whiteboardDisplayModes = [
  'inline',
  'fullscreen',
  'presentation',
] as const;
export type WhiteboardDisplayMode = (typeof whiteboardDisplayModes)[number];
export const isWhiteboardDisplayMode = (
  mode: string
): mode is WhiteboardDisplayMode => whiteboardDisplayModes.includes(mode);
